import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  IconButton,
  Tooltip,
  CircularProgress,
  Divider,
} from "@mui/material";
import {
  UploadFile,
  Description,
  Delete,
  InsertDriveFile,
  Image as ImageIcon,
  Close,
} from "@mui/icons-material";
import apiService from "../../../service/Api";
import { toast } from "react-toastify";

interface StreamProgramCardProps {
  streamId?: number;
}

interface StreamProgramResponse {
  id: number;
  file?: string;
  created_at: string;
  updated_at: string;
}

export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

const MAX_MB = 5;
const MAX_BYTES = MAX_MB * 1024 * 1024;

// Add formats that "programs" often come in:
const ACCEPT = [
  "application/pdf",
  "image/png",
  "image/jpeg",
  "application/msword", // .doc
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // .docx
  "application/vnd.ms-powerpoint", // .ppt
  "application/vnd.openxmlformats-officedocument.presentationml.presentation", // .pptx
];

const acceptAttr =
  ".pdf,.png,.jpg,.jpeg,.doc,.docx,.ppt,.pptx,application/pdf,image/png,image/jpeg";

const allowedLabel = "PDF, PNG, JPG/JPEG, DOC/DOCX, PPT/PPTX";

const getFileNameFromUrl = (url?: string) => {
  if (!url) return "Program";
  const last = url.split("/").pop() || "Program";
  return decodeURIComponent(last);
};

const formatFileSize = (bytes: number) => {
  const mb = bytes / 1024 / 1024;
  return `${mb.toFixed(2)} MB`;
};

const isImage = (file: File) => file.type.startsWith("image/");

const StreamProgramCard: React.FC<StreamProgramCardProps> = ({ streamId }) => {
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [existingProgram, setExistingProgram] =
    useState<StreamProgramResponse | null>(null);

  const inputRef = useRef<HTMLInputElement | null>(null);

  const fetchProgram = useCallback(async () => {
    if (!streamId) return;

    try {
      const res = await apiService.get<
        PaginatedResponse<StreamProgramResponse>
      >(`/api/streams/stream-program/?stream=${streamId}`);
      setExistingProgram(res.data.results?.[0] ?? null);
    } catch (err) {
      console.error("Failed to fetch program", err);
    }
  }, [streamId]);

  useEffect(() => {
    fetchProgram();
  }, [fetchProgram]);

  const resetPicker = () => {
    setFile(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  const handlePick = () => inputRef.current?.click();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    // type validation
    const typeOk = ACCEPT.includes(selectedFile.type);
    if (!typeOk) {
      toast.error(`Unsupported file type. Allowed: ${allowedLabel}`);
      resetPicker();
      return;
    }

    // size validation (optional but recommended)
    if (selectedFile.size > MAX_BYTES) {
      toast.error(`File too large. Max ${MAX_MB}MB allowed.`);
      resetPicker();
      return;
    }

    setFile(selectedFile);
  };

  const handleUpload = async () => {
    if (!file || !streamId) return;

    try {
      setIsUploading(true);

      const formData = new FormData();
      formData.append("stream_id", String(streamId));

      formData.append("file", file);

      await apiService.post("/api/streams/stream-program/", formData);

      toast.success("Program uploaded successfully");
      resetPicker();
      await fetchProgram();
    } catch (err) {
      console.error(err);
      toast.error("Failed to upload program");
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("Are you sure you want to delete this program?"))
      return;

    try {
      await apiService.delete(`/api/streams/stream-program/${id}/`);
      toast.success("Program deleted successfully");
      setExistingProgram(null);
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete program");
    }
  };

  const openUrl = (url: string) => window.open(url, "_blank");

  const previewSelected = () => {
    if (!file) return;
    openUrl(URL.createObjectURL(file));
  };

  const hasExisting = Boolean(existingProgram?.file);
  const showEmptyState = !hasExisting && !file;

  return (
    <Card
      sx={{
        boxShadow: 0,
        mt: 2,
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      <CardContent>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight={800}>
              Stream Program
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.25 }}
            >
              Allowed: {allowedLabel} • Max {MAX_MB}MB
            </Typography>
          </Box>

          <input
            ref={inputRef}
            type="file"
            accept={acceptAttr}
            hidden
            onChange={handleFileChange}
          />

          <Tooltip title="Upload program">
            <IconButton onClick={handlePick} color="primary">
              <UploadFile />
            </IconButton>
          </Tooltip>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Empty State */}
        {showEmptyState && (
          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              bgcolor: "action.hover",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <InsertDriveFile color="disabled" />
              <Box>
                <Typography fontWeight={700}>
                  No program uploaded yet
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Upload a file to attach a program to this stream.
                </Typography>
              </Box>
            </Box>

            <Button
              variant="contained"
              onClick={handlePick}
              startIcon={<UploadFile />}
            >
              Upload
            </Button>
          </Box>
        )}

        {/* Selected File (before upload) */}
        {file && (
          <Box
            sx={{
              p: 2,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
              {isImage(file) ? (
                <ImageIcon color="primary" />
              ) : (
                <Description color="primary" />
              )}
              <Box>
                <Typography fontWeight={800}>{file.name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatFileSize(file.size)} • {file.type || "unknown type"}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Button size="small" variant="outlined" onClick={previewSelected}>
                Preview
              </Button>

              <Button size="small" variant="outlined" onClick={handlePick}>
                Replace
              </Button>

              <Tooltip title="Remove selected file">
                <IconButton size="small" onClick={resetPicker}>
                  <Close fontSize="small" />
                </IconButton>
              </Tooltip>

              {isUploading ? (
                <CircularProgress size={24} />
              ) : (
                <Button size="small" variant="contained" onClick={handleUpload}>
                  Upload
                </Button>
              )}
            </Box>
          </Box>
        )}

        {/* Existing Program (already uploaded) */}
        {existingProgram?.file && !file && (
          <Box
            sx={{
              p: 2,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              cursor: "pointer",
              "&:hover": { bgcolor: "action.hover" },
            }}
            onClick={() => openUrl(existingProgram.file!)}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
              <Description color="primary" />
              <Box>
                <Typography fontWeight={800}>
                  {getFileNameFromUrl(existingProgram.file)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Click to open • Updated:{" "}
                  {new Date(existingProgram.updated_at).toLocaleString()}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Tooltip title="Delete program">
                <IconButton
                  size="small"
                  color="error"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(existingProgram.id);
                  }}
                >
                  <Delete fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default StreamProgramCard;
